import './dokumente.css';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { initialOf, t } from '../../i18n';
import { fmtDate } from '../../lib/format';
import { NoteRow } from '../belege/Bell';
import { useNotifications } from '../belege/shared';
import { jaPath } from '../jahresabschluss/labels';
import { docCount, dokumentePath, folderOf, FOLDERS, itemTitle } from './labels';
import { folderPath, useKanzleiDocs, useLetterList } from './Pages';

// Karte „Dokumente“ auf der Übersicht (Sam 11.10.): Ordner mit Inhalt und Anzahl, darunter die neuesten Unterlagen von
// der Kanzlei (Steuerbescheide, Dokumente, BWAs, je Geschäftsjahr der Jahresabschluss). Dokumente zum Unterschreiben
// stehen mit „Bitte unterschreiben“ bzw. „Unterschrieben“ drin. „Alle Ordner öffnen“ führt in den Bereich „Dokumente“.

// Meldung „Neu von Ihrer Kanzlei: …“, bis sie geöffnet ist; „Bitte unterschreiben: …“, bis unterschrieben ist
export function DokumenteNotices() {
  const notes = useNotifications();
  const items = (notes.data?.items ?? []).filter(
    (n) => (n.type === 'kanzlei_dokument' || n.type === 'jahresabschluss' || n.type === 'unterschrift') && !n.read,
  );
  if (!items.length) return null;
  return (
    <div className="notice" role="status">
      {items.map((n) => (
        <NoteRow key={n.id} item={n} />
      ))}
    </div>
  );
}

export function DokumenteCard({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const res = useKanzleiDocs(clientId);
  const letters = useLetterList(clientId);
  const [all, setAll] = useState(false);
  const items = res.data?.items ?? [];
  const ls = letters.data?.letters ?? [];
  if (!items.length && !ls.length) return null;
  const shown = all ? items : items.slice(0, 2);
  const open = (type: string, id: string) =>
    type === 'jahresabschluss'
      ? navigate(jaPath(clientId))
      : navigate({ search: type === 'bwa' ? `?bwa=${id}` : `?dok=${id}` }, { state: { sheet: true } });
  // Ordner mit Inhalt und Anzahl (Jahresabschluss: Dokumente aller Jahre)
  const folders = FOLDERS.map((f) => ({
    ...f,
    n: f.key === 'briefe' ? ls.length : items.filter((x) => folderOf(x) === f.key).reduce((sum, x) => sum + (x.count ?? 1), 0),
  })).filter((f) => f.n > 0);
  const total = folders.reduce((sum, f) => sum + f.n, 0);

  return (
    <div className="card docs-card">
      <h3>
        {t('Dokumente')} <span>{total}</span>
      </h3>
      <div className="docs-fold" role="group" aria-label={t('Ordner')}>
        {folders.map((f) => (
          <Link key={f.key} to={folderPath(clientId, f.key)}>
            {t(f.label)} <b>{f.n}</b>
          </Link>
        ))}
      </div>
      {items.length > 0 && (
        <>
          <div className="lines">
            {shown.map((x) => (
              <button key={`${x.type}-${x.id}`} type="button" className="ln lnb" onClick={() => open(x.type, x.id)}>
                <span>
                  {itemTitle(x)}
                  <small>
                    {t(FOLDERS.find((f) => f.key === folderOf(x))!.label)}
                    {x.count !== undefined ? ` · ${docCount(x.count)}` : ''} · {t('am {date}', { date: fmtDate(x.uploadedAt) })}
                  </small>
                </span>
                {x.sign ? (
                  <span className={x.sign === 'offen' ? 'st' : 'st ok'}>
                    {x.sign === 'offen' ? t('Bitte unterschreiben') : t('Unterschrieben')} ›
                  </span>
                ) : (
                  <span className={x.new ? 'st' : 'st ok'}>{x.new ? t('Neu') : t('Ansehen')} ›</span>
                )}
              </button>
            ))}
          </div>
          {items.length > 2 && (
            <button type="button" className="more" aria-expanded={all} onClick={() => setAll((v) => !v)}>
              {all ? t('Weniger anzeigen') : t('Alle von Ihrer Kanzlei anzeigen ({n})', { n: items.length })}
              <span aria-hidden="true">{all ? '▴' : '▾'}</span>
            </button>
          )}
        </>
      )}
      <Link className="more" to={dokumentePath(clientId)}>
        {t('Alle Ordner öffnen')} <span aria-hidden="true">›</span>
      </Link>
    </div>
  );
}

// Eintrag in der Liste der Bereiche
export function DokumenteListItem({ clientId }: { clientId: string }) {
  return (
    <Link className="item" to={dokumentePath(clientId)}>
      <span className="ico" aria-hidden="true">
        {initialOf('Dokumente')}
      </span>
      <span className="tx">
        <b>{t('Dokumente')}</b>
        <small>{t('Briefe, Steuerbescheide, Vollmachten & Co.')}</small>
      </span>
      <span className="chev" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}
